import React, { useMemo } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import LibraryRowActionsMenu from './LibraryRowActionsMenu';
import formatLibraryDate from '../utils/formatLibraryDate';
import getTranslationLanguageLabel from '../../../../../../utils/translation-language-label';
import { getCountryCode } from '../../model/library-mapper';
import SortableHeaderCell from '../../../table/sortable-header-cell';
import useTableSort from '../../../table/hooks/use-table-sort';
import { compareDates, compareText } from '../../../../../../utils/tableUtils';
import UpdateIndicator from '../../../../../atoms/update-indicator';

const SORT_FIELDS = {
  STATUS: 'status',
  PRE: 'publishedPreAt',
  PRO: 'publishedProAt',
};

const DATE_SORT_FIELDS = new Set([SORT_FIELDS.PRE, SORT_FIELDS.PRO]);

const moveRowsWithoutDateToEnd = (rows, field) => [
  ...rows.filter((row) => Boolean(row[field])),
  ...rows.filter((row) => !row[field]),
];

function LibraryTranslationsTable({
  rows,
  onDeployPre,
  onDeployPro,
  onDownload,
  onDelete,
  canPublishPre,
  canSeePublishPro,
  canPublishPro,
  canDeleteFile,
  onPreviewAs,
  t,
}) {
  const sortComparators = useMemo(() => ({
    [SORT_FIELDS.STATUS]: (a, b) => compareText(
      t(`library-status-${a.status}`),
      t(`library-status-${b.status}`)
    ),
    [SORT_FIELDS.PRE]: (a, b) => compareDates(a.publishedPreAt, b.publishedPreAt),
    [SORT_FIELDS.PRO]: (a, b) => compareDates(a.publishedProAt, b.publishedProAt),
  }), [t]);
  const { order, orderBy, sortedRows, handleRequestSort } = useTableSort(
    rows,
    sortComparators
  );
  const displayedRows = useMemo(
    () => DATE_SORT_FIELDS.has(orderBy)
      ? moveRowsWithoutDateToEnd(sortedRows, orderBy)
      : sortedRows,
    [orderBy, sortedRows]
  );

  return (
    <div className="library-view__table-wrapper">
      <Table stickyHeader className="library-view__table user-table" aria-label={t('library-table-aria')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('library-column-file')}</TableCell>
            <TableCell>{t('library-column-country')}</TableCell>
            <TableCell>{t('library-column-language')}</TableCell>
            <TableCell>{t('library-column-type')}</TableCell>
            <TableCell>{t('library-column-created')}</TableCell>
            <SortableHeaderCell
              field={SORT_FIELDS.STATUS}
              orderBy={orderBy}
              order={order}
              onRequestSort={handleRequestSort}
              label={t('library-column-status')}
            />
            <SortableHeaderCell
              field={SORT_FIELDS.PRE}
              orderBy={orderBy}
              order={order}
              onRequestSort={handleRequestSort}
              label={t('library-column-pre')}
            />
            <SortableHeaderCell
              field={SORT_FIELDS.PRO}
              orderBy={orderBy}
              order={order}
              onRequestSort={handleRequestSort}
              label={t('library-column-pro')}
            />
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {displayedRows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>
                {row.fileName}
                {row.showUpdateIndicator ? (
                  <UpdateIndicator
                    label={t('library-pending-keys-indicator', {
                      defaultValue: 'Contains new translation keys',
                    })}
                  />
                ) : null}
              </TableCell>
              <TableCell>
                {row.country
                  ? t(`countries-${getCountryCode(row.country)}`, { defaultValue: row.country })
                  : t('library-master-country')}
              </TableCell>
              <TableCell>
                {row.language
                  ? getTranslationLanguageLabel(row.language, getCountryCode(row.country), t)
                  : t('library-empty-none')}
              </TableCell>
              <TableCell>{row.type || t('library-empty-unknown')}</TableCell>
              <TableCell>{formatLibraryDate(row.uploadedAt, t('library-status-pending'))}</TableCell>
              <TableCell>{t(`library-status-${row.status}`)}</TableCell>
              <TableCell>{formatLibraryDate(row.publishedPreAt, t('library-status-pending'))}</TableCell>
              <TableCell>{formatLibraryDate(row.publishedProAt, t('library-status-pending'))}</TableCell>
              <TableCell align="right">
                <LibraryRowActionsMenu
                  row={row}
                  onDeployPre={onDeployPre}
                  onDeployPro={onDeployPro}
                  onDownload={onDownload}
                  onDelete={onDelete}
                  canPublishPre={canPublishPre}
                  canSeePublishPro={canSeePublishPro}
                  canPublishPro={canPublishPro}
                  canDeleteFile={canDeleteFile}
                  onPreviewAs={onPreviewAs}
                  t={t}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default LibraryTranslationsTable;
